import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid-premium';
import { LineChart } from 'lucide-react';

import type { GetTransactionValuationsQuery } from '@graphql/__generated__/graphql';
import { RaDataGrid } from '@modules/_shared/components/data-grid/RaDataGrid';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { getAppraisalPerception } from '@modules/_shared/utils/appraisal';
import {
  formatAppraisalValues,
  formatPostcodeLocality,
} from '@modules/_shared/utils/formatting';
import {
  getHorizonLabel,
  translateAppraisalReason,
} from '@modules/_shared/utils/lead-labels';
import { GET_TRANSACTION_VALUATIONS } from '@modules/transactions/transactionQueries';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';
import { formatNumberByPrecision } from '@shared/utils/number-format';

type Row = GetTransactionValuationsQuery['transaction_valuations'][number];

type TransactionAnalysisProps = {
  transactionId: string;
};

export const TransactionAnalysis = ({
  transactionId,
}: TransactionAnalysisProps) => {
  const { t, locale } = useLocale();
  const { me } = useAppData();
  const { data, loading, error } = useQuery(GET_TRANSACTION_VALUATIONS, {
    variables: { transactionId },
  });

  const handleRowClick = (params: { row: { lead_id: string } }) => {
    const leadUrl = `/leads/${params.row.lead_id}`;
    window.open(leadUrl, '_blank');
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'created_at',
        headerName: t('Date'),
        width: 110,
        minWidth: 100,
        flex: 0.8,
        sortable: false,
        renderCell: ({ row }: { row: Row }) => {
          const showRequalifiedDate = row.requalified_at;
          const displayDate = row.requalified_at ?? row.created_at;

          if (!me?.is_admin) {
            return <TimeAgo dateString={displayDate} />;
          }

          return (
            <Stack
              sx={{
                justifyContent: 'center',
                height: '100%',
              }}
            >
              {showRequalifiedDate && (
                <Typography variant="body2">
                  ♺&nbsp;
                  <TimeAgo dateString={row.requalified_at} />
                </Typography>
              )}
              <Typography
                variant={showRequalifiedDate ? 'caption' : 'body2'}
                sx={{
                  color: 'text.secondary',
                }}
              >
                <TimeAgo dateString={row.created_at} />
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'first_name',
        headerName: t('Contact'),
        width: 220,
        minWidth: 180,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2">
              {[row.first_name, row.last_name].filter(Boolean).join(' ')}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'route',
        headerName: t('Address'),
        width: 220,
        minWidth: 180,
        flex: 1,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack
            sx={{
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2">
              {[row.route, row.street_number].filter(Boolean).join(' ')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {formatPostcodeLocality(row.postcode, row.locality)}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'appraisal_perceived_value',
        headerName: t('Value'),
        width: 180,
        minWidth: 160,
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              height: '100%',
              gap: 1,
            }}
          >
            {getAppraisalPerception(
              row.appraisal_perception,
              row.appraisal_perceived_value,
              row.appraisal_max,
              row.appraisal_min,
            )}
            <Stack>
              <Typography variant="body2">
                {formatAppraisalValues(
                  row.appraisal_min,
                  row.appraisal_max,
                  locale,
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {row.appraisal_perceived_value != null
                  ? formatNumberByPrecision(
                      row.appraisal_perceived_value,
                      locale,
                    )
                  : '-'}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        field: 'appraisal_reason',
        headerName: t('Intention'),
        width: 150,
        minWidth: 130,
        flex: 0.8,
        sortable: false,
        renderCell: ({ row }) => {
          const horizonLabel =
            row.appraisal_reason != null &&
            (row.lead?.sale_horizon || row.lead?.buy_horizon)
              ? getHorizonLabel(t, {
                  appraisalReason: row.appraisal_reason,
                  saleHorizon: row.lead?.sale_horizon,
                  buyHorizon: row.lead?.buy_horizon,
                })
              : null;

          return (
            <Stack
              sx={{
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2">
                {row.appraisal_reason != null
                  ? translateAppraisalReason(t, row.appraisal_reason)
                  : '-'}
              </Typography>
              {horizonLabel != null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {horizonLabel}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: 'property_type',
        headerName: t('Property type'),
        width: 180,
        minWidth: 150,
        flex: 0.9,
        sortable: false,
        renderCell: ({ row }) => {
          const propertyType = row.lead?.property?.property_type;

          // Use composite type for more specific categorization, fallback to main type
          const typeLabel =
            propertyType?.composite_type_object?.label ||
            propertyType?.main_type_object?.label;
          const specificLabel = propertyType?.label;

          return typeLabel ? (
            <Stack
              sx={{
                height: '100%',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2">{typeLabel}</Typography>
              {specificLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {specificLabel}
                </Typography>
              )}
            </Stack>
          ) : (
            '-'
          );
        },
      },
    ],
    [t, locale, me?.is_admin],
  );

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {t('Error loading valuations')}
      </Alert>
    );
  }

  const valuations = data?.transaction_valuations || [];

  if (!loading && valuations.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
        }}
      >
        <Box sx={{ color: customPalette.gray400, mb: 2 }}>
          <LineChart size={64} />
        </Box>
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('No valuations found for this address')}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            mt: 1,
          }}
        >
          {t(
            'Valuations will appear here when leads complete appraisals at this property address',
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {valuations.length === 1
          ? t('Valuation on this address')
          : t('{{count}} valuations on this address', {
              count: valuations.length,
            })}
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <RaDataGrid
        rows={valuations}
        columns={columns}
        loading={loading}
        getRowId={row => row.lead_id}
        onRowClick={handleRowClick}
        disableRowSelectionOnClick
        disableColumnMenu
        autoHeight
        paginationMode="client"
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{
          '& .MuiDataGrid-row': {
            backgroundColor: 'background.paper',
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Box>
  );
};
